import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { authService } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import { ROUTES } from '@/constants';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await authService.resetPassword(email);
    if (error) {
      toast('Failed', error.message, 'destructive');
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl text-center"
        >
          <div className="h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Check your email</h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">
            A password reset link has been sent to {email}.
          </p>
          <Button onClick={() => navigate(ROUTES.LOGIN)} className="mt-6 w-full">
            Back to sign in
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-brand-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">K</div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Forgot password?</h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">Enter your email to reset your password</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Sending...' : 'Send reset link'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link to={ROUTES.LOGIN} className="text-sm text-neutral-600 dark:text-neutral-400 hover:underline">
            ← Back to sign in
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
